import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faPlus, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { CRM_NAV, BAND_COLORS } from './nav';

const FeedbackNPS: React.FC = () => {
    const { api } = useInternalApi();
    const [entries, setEntries] = useState<any[]>([]);
    const [summary, setSummary] = useState<any>(null);
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({ respondent_label: '', score: '', comment: '', source: 'survey', principal_type: '', principal_id: '' });

    useEffect(() => { document.title = 'Feedback & NPS - Uzima Internal'; }, []);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get('crm-api/feedback/');
            if (r.data?.status === 'success') {
                setEntries(r.data.data.entries || []);
                setSummary(r.data.data.summary);
                setNote(r.data.data.note || '');
            }
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const submit = async () => {
        if (!form.score && !form.comment.trim()) {
            Swal.fire({ icon: 'warning', title: 'Nothing to record', text: 'Provide a score, a comment, or both.' });
            return;
        }
        try {
            await api.post('crm-api/feedback/', { ...form, score: form.score === '' ? null : Number(form.score) });
            setShowAdd(false);
            setForm({ respondent_label: '', score: '', comment: '', source: 'survey', principal_type: '', principal_id: '' });
            load();
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not record feedback.' });
        }
    };

    return (
        <InternalShell activeDashboard="customer_relations" subNav={CRM_NAV}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faStar} className="me-2 text-primary" />Feedback & NPS</h2>
                    <p className="text-muted mb-0">Recommendation scores and what people actually said</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowAdd(true)}><FontAwesomeIcon icon={faPlus} className="me-2" />Record Feedback</button>
            </div>

            {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
                <>
                    {summary && (
                        <div className="row mb-4">
                            <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                                <div className="text-muted small mb-1">Net Promoter Score</div>
                                <h3 className={summary.nps === null ? 'text-muted' : summary.nps >= 30 ? 'text-success' : summary.nps >= 0 ? 'text-warning' : 'text-danger'}>
                                    {summary.nps === null ? '—' : summary.nps}
                                </h3>
                            </div></div></div>
                            <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                                <div className="text-muted small mb-1">Promoters</div><h4 className="text-success">{summary.promoters}</h4>
                            </div></div></div>
                            <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                                <div className="text-muted small mb-1">Passives</div><h4 className="text-warning">{summary.passives}</h4>
                            </div></div></div>
                            <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                                <div className="text-muted small mb-1">Detractors</div><h4 className="text-danger">{summary.detractors}</h4>
                            </div></div></div>
                        </div>
                    )}

                    <div className="alert alert-secondary small"><FontAwesomeIcon icon={faInfoCircle} className="me-2" />{note}</div>

                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-0">
                            {entries.length === 0 ? (
                                <div className="text-center py-5 text-muted">No feedback recorded yet.</div>
                            ) : (
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light"><tr><th>Respondent</th><th>Score</th><th>Comment</th><th>Source</th><th>Recorded by</th><th>When</th></tr></thead>
                                    <tbody>
                                        {entries.map(e => (
                                            <tr key={e.id}>
                                                <td>{e.respondent_label || <span className="text-muted">Anonymous</span>}</td>
                                                <td>{e.score === null ? <span className="text-muted small">text only</span> :
                                                    <span className={`badge bg-${BAND_COLORS[e.band] || 'secondary'}`}>{e.score}/10</span>}</td>
                                                <td className="small">{e.comment || '—'}</td>
                                                <td className="small">{e.source}</td>
                                                <td className="small">{e.recorded_by || '—'}</td>
                                                <td className="small">{new Date(e.created).toLocaleDateString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </>
            )}

            {showAdd && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Record Feedback</h5>
                                <button type="button" className="btn-close" onClick={() => setShowAdd(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Respondent</label>
                                    <input className="form-control" value={form.respondent_label} onChange={(e) => setForm(f => ({ ...f, respondent_label: e.target.value }))} />
                                </div>
                                <div className="row g-3 mb-3">
                                    <div className="col-6">
                                        <label className="form-label">Score (0–10)</label>
                                        <input type="number" min={0} max={10} className="form-control" value={form.score}
                                            onChange={(e) => setForm(f => ({ ...f, score: e.target.value }))} />
                                        <div className="form-text">Leave blank for text-only feedback.</div>
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Source</label>
                                        <select className="form-select" value={form.source} onChange={(e) => setForm(f => ({ ...f, source: e.target.value }))}>
                                            <option value="survey">Survey</option><option value="interview">Interview</option>
                                            <option value="support">Support interaction</option><option value="unsolicited">Unsolicited</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Comment</label>
                                    <textarea className="form-control" rows={4} value={form.comment} onChange={(e) => setForm(f => ({ ...f, comment: e.target.value }))} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowAdd(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={submit}>Record</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default FeedbackNPS;
